// UI strings of the layout v2 foundation (docs/design/SIVUPALSTA.md): the working margin (lane B's
// head, the word digest, quick settings), the back bar, the one player bar and the week road.
// Finnish follows docs/FINNISH-STYLE.md: no internal jargon ("sovellus tunnistaa", never
// "analysaattori"), "sinä" form; nothing is truncated.
import { defineArea } from '../area';

export const marginFi = {
  /** The PC margin (lane B) as a landmark. */
  lane: 'Sivupalsta',
  close: 'Sulje',
  /** Screen readers: what opened in lane B ("Avattu sivupalstalle: Sana valar"). */
  opened: (what: string) => `Avattu sivupalstalle: ${what}`,
  /** Screen readers, on a line of word links (its one tab stop). */
  wordLineHint: 'Nuolinäppäimet siirtävät sanasta toiseen. Enter avaa sanan.',
  quick: {
    title: 'Pika-asetukset',
    /** The phone top bar button and the PC margin head button (its visible text on PC). */
    open: 'Pika-asetukset',
    all: 'Kaikki asetukset',
    mute: 'Mykistä',
    unmute: 'Palauta ääni',
    lineTexts: 'Repliikkien tekstit',
    textsShown: 'Näkyvissä',
    textsHidden: 'Piilossa, kunnes napautat',
    /** The shared pace in one line: today's new words a day. */
    paceValue: (n: number) => (n === 1 ? '1 uusi sana päivässä' : `${n} uutta sanaa päivässä`),
    fewer: 'Vähemmän uusia sanoja',
    more: 'Enemmän uusia sanoja',
    /** Read-only shared value: who sets it. */
    setBy: (name: string) => `${name} asettaa`,
    setByYou: 'Asetat tämän teille molemmille.',
    saved: 'Tallennettu teille molemmille.',
  },
  word: {
    title: 'Sana',
    open: 'Avaa sana',
    unknown: 'Tätä muotoa ei vielä tunnisteta.',
    search: 'Etsi sanakirjasta',
    loading: 'Sanaa tunnistetaan…',
    /** The form index failed to load: inflected forms cannot be recognised now. */
    failed: 'Taivutusmuotoja ei voitu ladata.',
    /** An ambiguous form: its readings, one chosen at a time. */
    readings: 'Muoto voi olla',
    /** The tapped form is the word's dictionary form (after its word class: "adjektiivi · perusmuoto"). */
    baseForm: 'perusmuoto',
    /** A word with no table: it has one form only ("partikkeli · ei taivu"). */
    noInflection: 'ei taivu',
    /** Before a second reading of the same form that does not merge with the first. */
    or: 'tai',
    /** The rest of a form's readings, behind a disclosure. */
    moreReadings: (n: number) => `${n} muuta tulkintaa`,
    voices: 'Äänet',
    peterson: 'Peterson',
    you: 'Sinä',
    record: 'Äänitä',
    stopRecording: 'Lopeta äänittäminen',
    noVoice: 'ei äänitettä',
    example: 'Esimerkki',
  },
  back: {
    /** Screen readers: "Takaisin: Sanakirja". The bar shows "← Sanakirja". */
    label: (parent: string) => `Takaisin: ${parent}`,
  },
  player: {
    label: 'Soitin',
    play: 'Kuuntele',
    pause: 'Tauko',
    loop: 'Silmukka',
    slow: 'Hidas',
    source: (n: number, of: number) => `äänite ${n}/${of}`,
    close: 'Sulje soitin',
    failed: 'Äänitettä ei voitu toistaa.',
  },
  notes: {
    /** A sidenote folded to a tick (1180–1599px while the lane is taken, or below 1180px). */
    show: 'Näytä huomautus',
    hide: 'Piilota huomautus',
  },
  road: {
    label: 'Viikot Halloweeniin 2027',
    week: (n: number) => `Viikko ${n}`,
    open: 'avoinna',
    passed: 'takana',
    opens: (date: string) => `avautuu ${date}`,
    goal: 'Halloween 2027',
    you: 'sinä',
    youPassed: 'sinä läpäisit',
    partnerPassed: (name: string) => `${name} läpäisi`,
  },
};

export const marginEn: typeof marginFi = {
  lane: 'Margin',
  close: 'Close',
  opened: (what) => `Opened in the margin: ${what}`,
  wordLineHint: 'The arrow keys move from word to word. Enter opens the word.',
  quick: {
    title: 'Quick settings',
    open: 'Quick settings',
    all: 'All settings',
    mute: 'Mute',
    unmute: 'Unmute',
    lineTexts: 'Line texts',
    textsShown: 'Shown',
    textsHidden: 'Hidden until you tap',
    paceValue: (n) => (n === 1 ? '1 new word a day' : `${n} new words a day`),
    fewer: 'Fewer new words',
    more: 'More new words',
    setBy: (name) => `Set by ${name}`,
    setByYou: 'You set this for both of you.',
    saved: 'Saved for both of you.',
  },
  word: {
    title: 'Word',
    open: 'Open the word',
    unknown: 'This form is not recognised yet.',
    search: 'Search the dictionary',
    loading: 'Recognising the word…',
    failed: 'The inflected forms could not be loaded.',
    readings: 'This form can be',
    baseForm: 'dictionary form',
    noInflection: 'does not inflect',
    or: 'or',
    moreReadings: (n) => `${n} more readings`,
    voices: 'Voices',
    peterson: 'Peterson',
    you: 'You',
    record: 'Record',
    stopRecording: 'Stop recording',
    noVoice: 'no recording',
    example: 'Example',
  },
  back: {
    label: (parent) => `Back: ${parent}`,
  },
  player: {
    label: 'Player',
    play: 'Listen',
    pause: 'Pause',
    loop: 'Loop',
    slow: 'Slow',
    source: (n, of) => `recording ${n}/${of}`,
    close: 'Close the player',
    failed: 'The recording could not be played.',
  },
  notes: {
    show: 'Show the note',
    hide: 'Hide the note',
  },
  road: {
    label: 'Weeks to Halloween 2027',
    week: (n) => `Week ${n}`,
    open: 'open',
    passed: 'behind you',
    opens: (date) => `opens ${date}`,
    goal: 'Halloween 2027',
    you: 'you',
    youPassed: 'you passed',
    partnerPassed: (name) => `${name} passed`,
  },
};

export const area = defineArea('margin', marginFi, marginEn);

declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof marginFi> {}
}
